import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import axios from "../../../lib/axios";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { IThread, IMessage } from "../../../types/api/messaging";

export const useThreadList = (initialParams: any = {}) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.MESSAGE_THREAD_LIST, { ...params, total: null }],
    queryFn: async () => { const res = await axios.get(API_ENDPOINTS.MESSAGE_THREAD_LIST, { params }); return res.data; },
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: IThread) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: {}, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 15000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useThreadMessages = (threadId: number | null) =>
  useQuery({
    queryKey: [API_ENDPOINTS.MESSAGE_THREAD_MESSAGES, threadId],
    queryFn: async () => {
      const url = API_ENDPOINTS.MESSAGE_THREAD_MESSAGES.replace(":id", String(threadId));
      const res = await axios.get(url);
      return res.data;
    },
    enabled: !!threadId,
    staleTime: 10000,
    refetchOnWindowFocus: false,
  });

export const useCreateThread = (options?: { onSuccess?: (data: IThread) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { subject?: string; assigned_to?: number; body: string }) => {
      const res = await axios.post(API_ENDPOINTS.MESSAGE_THREAD_CREATE, data);
      return res.data;
    },
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.MESSAGE_THREAD_LIST] }); options?.onSuccess?.(res.data as IThread); },
    onError: options?.onError,
  });
};

export const useSendMessage = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { thread_id: number; body: string }) => {
      const res = await axios.post(API_ENDPOINTS.MESSAGE_SEND, data);
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.MESSAGE_THREAD_MESSAGES, vars.thread_id] });
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.MESSAGE_THREAD_LIST] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

export const useResolveThread = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => { const res = await axios.post(API_ENDPOINTS.MESSAGE_THREAD_RESOLVE, { id }); return res.data; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.MESSAGE_THREAD_LIST] }); options?.onSuccess?.(); },
  });
};
